import { LitElement, html, nothing } from 'lit';
import { map } from 'lit/directives/map.js';
import { ref } from 'lit/directives/ref.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { EventEmitterMixin } from './event_emitter';

/** API error messages mapped to `window.theme.strings.form_errors` keys ending in `_html` */
const SERVER_ERROR_TO_FORM_ERRORS_HTML_KEY = {
  'Phone has already been taken': 'phone_taken_html',
};

function extractRawErrorMessage(error) {
  if (typeof error === 'string') return error;
  if (!error || typeof error !== 'object') return '';
  const m = error.message;
  if (typeof m === 'string') return m;
  if (m && typeof m === 'object' && m !== null && 'value' in m) {
    return String(m.value);
  }
  return '';
}

/**
 * @returns {{ plain: string, html: string | null }}
 */
function resolveFieldErrorMessage(rawMessage) {
  const str =
    typeof rawMessage === 'string' ? rawMessage : String(rawMessage ?? '');
  const htmlKey = SERVER_ERROR_TO_FORM_ERRORS_HTML_KEY[str];
  if (!htmlKey || !htmlKey.endsWith('_html')) {
    return { plain: str, html: null };
  }
  const html = window.theme?.strings?.form_errors?.[htmlKey];
  if (!html || typeof html !== 'string') {
    return { plain: str, html: null };
  }
  const plain = html.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
  return { plain, html };
}

/**
 * Base form component that handles form validation, submission, and error handling
 * @extends {LitElement}
 */
export default class BoilerplateForm extends EventEmitterMixin(LitElement) {
  /** @type {string[]} Controls that support checked state */
  _controlsWithChecked = ['input', 'radio'];

  /** @type {string[]} Controls that support value property */
  _controlsWithValue = ['input', 'textarea', 'select'];

  /** @type {string[]} Controls that can trigger form submission */
  _controlsThatSubmit = ['input', 'button'];

  /**
   * Creates shadow DOM for the component
   * @returns {Element} The component's root element
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Component properties
   * @returns {Object} Property definitions
   */
  static get properties() {
    return {
      novalidate: { type: Boolean, reflect: true },
      formData: { type: Object },
      objectData: { type: Object },
      loading: { type: Boolean, state: true },
      busy: { type: Boolean, state: true },
      errors: { type: Array, state: true },
      genericErrorSegments: { type: Array, state: true },
      action: { type: String },
      method: { type: String },
      enctype: { type: String },
      multiStep: { type: Array },
      success: { type: Boolean, state: true },
      currentStep: { type: Object, state: true },
      id: { type: String, attribute: true },
      token: { type: String, attribute: true },
      value: { type: String, attribute: true },
      region: { type: String, attribute: true },
    };
  }

  /**
   * Creates a new BoilerplateForm instance
   */
  constructor() {
    super();

    this.novalidate = this.getAttribute('novalidate') === 'true';
    this.errors = {};
    this.genericErrorSegments = [];
    this.loading = false;
    this._attemptedSubmit = false;
    this.id = this.getAttribute('id') || this.tagName.toLowerCase();
    this.useUrlParams = true;
    this.multiStep = [
      { name: 'initial', render: this.renderInitial.bind(this) },
      { name: 'success', render: this.renderSuccess.bind(this) },
    ];

    this.dataset.formId = crypto.randomUUID();

    this.headerOffset = document.querySelector('.header-wrapper').offsetHeight;

    this._setupValidityListeners();

    this.addEventListener('click', this.handleClick);
    this.addEventListener(`submit:${this.id}`, this.handleSubmit);

    if (this.useUrlParams) {
      window.addEventListener('popstate', () => {
        this.handleFormState();
      });
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.useUrlParams) {
      this.handleFormState();
    } else {
      // Initialize to first step when URL params are disabled
      this.currentStep = this.multiStep.find((step) => step.name === 'initial');
    }
  }

  updateFormState(stepName, reload = false, override = false) {
    if (!this.id) return;

    if (this.useUrlParams) {
      const params = new URLSearchParams(window.location.search);
      params.set(`${this.id}`, stepName);

      if (override) {
        params.set(`override`, 'true');
      }

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState({}, '', newUrl);

      if (reload) {
        window.location.href = newUrl;
        return;
      }
    }

    const step = this.multiStep.find((step) => step.name === stepName);

    step
      ? (this.currentStep = step)
      : (this.currentStep = this.multiStep.find(
          (step) => step.name === 'initial'
        ));
  }

  handleFormState() {
    let step;

    if (this.useUrlParams) {
      const params = new URLSearchParams(window.location.search);
      const savedState = params.get(this.id);
      step = this.multiStep.find((step) => step.name === savedState);
    }

    step
      ? (this.currentStep = step)
      : (this.currentStep = this.multiStep.find(
          (step) => step.name === 'initial'
        ));

    if (this.useUrlParams) {
      this.updateFormState(this.currentStep.name);
    }
  }

  /**
   * Handles form submission
   * @param {Event} event - The submit event
   */
  handleSubmit(event) {
    throw new Error('handleSubmit, not implemented', { cause: event });
  }

  convertFormDataToObject(formData) {
    const object = {};
    for (const [key, value] of formData.entries()) {
      object[key] = value;
    }
    return object;
  }

  convertObjectToFormData(object) {
    const formData = new FormData();
    for (const [key, value] of Object.entries(object)) {
      formData.append(key, value);
    }
    return formData;
  }

  /**
   * Sets up validity change listeners
   * @private
   */
  _setupValidityListeners() {
    this.addEventListener('validitychange', () => {
      if (
        this._attemptedSubmit &&
        this.checkValidity() &&
        Object.keys(this.errors).length > 0
      ) {
        this.errors = {};
        this.genericErrorSegments = [];
      }
    });
  }

  /**
   * Emits submit event with form data
   * @returns {boolean} Whether submission was successful
   */
  submitEmitter() {
    const formData = this.getFormData();
    if (!this.novalidate && !this.reportValidity()) {
      this.setInteracted();
      const firstErrorField = this.getFormElements().find(
        (element) => element.required && !element.validity.valid
      );

      if (firstErrorField) {
        firstErrorField.focus();
        return false;
      }
    }

    if (this.loading) return false;

    this.formData = formData;
    this.objectData = this.convertFormDataToObject(formData);

    this.emitEvent(`submit:${this.id}`, { detail: formData });
    this.loading = true;
    return true;
  }

  /**
   * Resets form elements to default values
   */
  resetEmitter() {
    if (this.form) {
      this.form.reset();
    }

    const formElements = this.getFormElements();
    for (const element of formElements) {
      const tagName = element.tagName.toLowerCase();

      if (
        !this._controlsWithValue.includes(tagName) &&
        !this._controlsWithChecked.includes(tagName)
      ) {
        continue;
      }

      if (tagName === 'select') {
        for (const option of element.options) {
          option.selected = option.defaultSelected;
        }
      } else if (
        (tagName === 'input' &&
          (element.type === 'checkbox' || element.type === 'radio')) ||
        (tagName !== 'input' && this._controlsWithChecked.includes(tagName))
      ) {
        element.checked = element.hasAttribute('checked');
      } else if (this._controlsWithValue.includes(tagName)) {
        element.value = element.defaultValue;
      }
    }

    this.errors = {};
    this.genericErrorSegments = [];

    this.emitEvent('reset');
  }

  /**
   * Handles successful form submission
   */
  handleSuccess() {
    this.errors = {};
    this.genericErrorSegments = [];
    this.loading = false;

    window.announcements?.success('Form submitted successfully');

    this.updateFormState('success');

    // this.formData - FormData object
    // this.objectData - Object with the form data
  }

  _clearFieldValidationState() {
    const selector =
      'input, float-field, float-masked-field, float-phone-field, float-date-field, button, radio, float-select, select, float-textarea, float-country, float-state, float-checkbox';
    this.querySelectorAll(selector).forEach((el) => {
      if (el.disabled) return;
      if (typeof el.setValidationDisplay === 'function') {
        el.setValidationDisplay({ plain: '' });
      } else if (typeof el.setCustomValidity === 'function') {
        el.setCustomValidity('');
      }
    });
  }

  /**
   * Handles form validation errors
   * @param {Array<{field: string, message: string}>} errors - Array of error objects
   */
  handleErrors(errors) {
    this.loading = false;

    this.errors = {};

    if (typeof errors === 'string') {
      const resolved = resolveFieldErrorMessage(errors);
      this.errors._generic = [resolved.plain];
      this.genericErrorSegments = [
        { plain: resolved.plain, html: resolved.html },
      ];
      window.announcements?.error(
        `Form submission failed. ${resolved.plain}`
      );
      return;
    }

    this._clearFieldValidationState();
    this.genericErrorSegments = [];

    /** @type {Map<string, string>} */
    const fieldHtml = new Map();

    for (const error of errors) {
      const rawField = error.field;
      const fieldKey = Array.isArray(rawField) ? rawField[0] : rawField;
      const key = fieldKey ?? '_generic';

      const rawMsg =
        typeof error === 'string' ? error : extractRawErrorMessage(error);
      const resolved = resolveFieldErrorMessage(rawMsg);

      if (typeof key === 'string' && key.includes('metafields')) {
        const metafieldFields = this.querySelectorAll(`[name*="metafield"]`);

        if (!metafieldFields?.length) continue;

        const whatField = key[1];

        const field = metafieldFields[whatField];
        if (!field) continue;

        if (resolved.html && typeof field.setValidationDisplay === 'function') {
          field.setValidationDisplay({
            plain: resolved.plain,
            html: resolved.html,
          });
        } else {
          field.setCustomValidity(resolved.plain);
        }
      }

      if (!this.errors[key]) {
        this.errors[key] = [];
      }

      this.errors[key].push(resolved.plain);

      if (key === '_generic') {
        this.genericErrorSegments.push({
          plain: resolved.plain,
          html: resolved.html,
        });
      }

      if (key !== '_generic' && resolved.html) {
        fieldHtml.set(key, resolved.html);
      }
    }

    for (const [fieldName, errorArray] of Object.entries(this.errors)) {
      if (fieldName === '_generic') continue;

      const field = this.querySelector(`[name="${fieldName}"]`);
      if (!field) continue;

      const combinedPlain = errorArray.join(' & ');
      const html = fieldHtml.get(fieldName);
      if (html && typeof field.setValidationDisplay === 'function') {
        field.setValidationDisplay({ plain: combinedPlain, html });
      } else {
        field.setCustomValidity(errorArray);
      }
    }

    if (this.errors._generic && this.errors._generic.length > 0) {
      const errorMessage = `Form submission failed. ${this.errors._generic.join(
        '. '
      )}`;
      window.announcements?.error(errorMessage);
    }
  }

  /**
   * Gets all form control elements
   * @returns {Element[]} Array of form elements
   */
  getFormElements() {
    const formElements = [];
    const elements = this.querySelectorAll(
      'input, float-field, float-masked-field, float-phone-field, float-date-field, button, radio, float-select, select, float-textarea, float-country, float-state, float-checkbox'
    );

    elements?.forEach((element) => {
      if (!element.disabled) {
        formElements.push(element);
      }
      const children = Array.from(element.getElementsByTagName('*')).filter(
        (el) => !el.disabled
      );
      formElements.push(...children);
    });

    return formElements;
  }

  /**
   * Gets form data from all form elements
   * @returns {FormData} Form data object
   */
  getFormData() {
    const formData = new FormData();

    const formElements = this.getFormElements();
    const uniqueNames = new Map();

    for (const element of formElements) {
      const tagName = element.tagName.toLowerCase();

      if (
        (!this._controlsWithValue.includes(tagName) &&
          !this._controlsWithChecked.includes(tagName)) ||
        !element.name
      ) {
        continue;
      }

      if (tagName === 'select') {
        for (let i = 0; i < element.options.length; i++) {
          const option = element.options[i];
          if (option.selected) {
            uniqueNames.set(element.name, option.value);
          }
        }
      } else if (
        this._controlsWithChecked.includes(tagName) &&
        element.checked
      ) {
        uniqueNames.set(element.name, element.value || 'on');
      } else if (
        this._controlsWithValue.includes(tagName) &&
        element.type !== 'checkbox' &&
        element.type !== 'radio' &&
        element.type !== 'submit'
      ) {
        uniqueNames.set(element.name, element.value);
      }
    }

    // Remove duplicates
    for (const [name, value] of uniqueNames.entries()) {
      formData.append(name, value);
    }

    return formData;
  }

  /**
   * Reports validity of all form elements
   * @returns {boolean} Whether all elements are valid
   */
  reportValidity() {
    const formElements = this.getFormElements();
    return !formElements.some(
      (element) =>
        typeof element.reportValidity === 'function' &&
        element.reportValidity() === false
    );
  }

  setInteracted() {
    const formElements = this.getFormElements();
    for (const element of formElements) {
      if (element.hasInteracted !== undefined) {
        element.hasInteracted = true;
      }
    }
  }

  /**
   * Checks validity of all form elements without reporting
   * @returns {boolean} Whether all elements are valid
   */
  checkValidity() {
    const formElements = this.getFormElements();
    return !formElements.some(
      (element) =>
        typeof element.reportValidity === 'function' &&
        element.reportValidity() === false
    );
  }

  /**
   * Handles click events on form controls
   * @param {Event} event - The click event
   * @returns {boolean} Whether event was handled
   */
  handleClick(event) {
    const targetElement = event.target;
    if (
      this._controlsThatSubmit.includes(targetElement.tagName.toLowerCase()) &&
      targetElement.type?.toLowerCase() === 'submit' &&
      targetElement.closest('[data-form-id]')?.dataset.formId ===
        this.dataset.formId
    ) {
      this.submitEmitter();
    } else if (
      targetElement.type?.toLowerCase() === 'reset' &&
      targetElement.closest('[data-form-id]')?.dataset.formId ===
        this.dataset.formId
    ) {
      this.resetEmitter();
    }

    return true;
  }

  renderFormElements() {
    return html`a form needs inputs!! renderFormElements method`;
  }

  renderButton() {
    return html`<button
      type="submit"
      @click=${this.handleClick}
      ?disabled=${this.loading || !this.checkValidity()}
    >
      ${this.loading ? html`<bouncing-ellipsis></bouncing-ellipsis>` : 'Submit'}
    </button>`;
  }

  renderSuccess() {
    return html`<div>Success</div>`;
  }

  renderErrors() {
    const segments =
      this.genericErrorSegments?.length > 0
        ? this.genericErrorSegments
        : (this.errors._generic || []).map((plain) => ({
            plain,
            html: null,
          }));

    if (!segments.length) {
      return nothing;
    }

    return html`<p
      class="form_generic_errors"
      aria-live="assertive"
      aria-atomic="true"
      role="status"
    >
      <svg
        title="Warning symbol"
        alt="Warning symbol"
        width="13"
        height="13"
        viewBox="0 0 29 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M27.723 19.2958L16.594 1.58862C15.9495 0.563405 15.0804 0 14.1436 0C13.2061 0 12.3366 0.563859 11.6923 1.58953L0.589826 19.2689C-0.0544873 20.2939 -0.175124 21.2728 0.248597 22.0282C0.675535 22.7884 1.59053 23.2059 2.82585 23.2073L25.4945 23.2244H25.4752V23.2266C26.5823 23.2266 27.6328 22.811 28.0593 22.0532C28.4831 21.3001 28.3673 20.3214 27.723 19.2958ZM14.7613 19.6921C14.5966 19.8546 14.3744 19.9469 14.1414 19.9469C13.9088 19.9469 13.6825 19.8537 13.5171 19.6912C13.353 19.5287 13.2599 19.3019 13.2602 19.0701C13.2606 18.8396 13.3151 18.6142 13.4796 18.453C13.6476 18.2889 13.79 18.1951 14.0115 18.1951L14.0529 18.1946C14.285 18.1946 14.5607 18.2889 14.7276 18.4539C14.8925 18.6162 15.0109 18.841 15.0104 19.0719C15.0102 19.3042 14.9275 19.5303 14.7613 19.6921ZM14.1441 16.4426C13.9075 16.4426 13.688 16.3517 13.5207 16.1862C13.3534 16.0206 13.2629 15.8003 13.2631 15.5658L13.1703 7.66426C13.1712 7.18062 13.5134 6.78835 13.9564 6.78835H14.0582C14.2951 6.78835 14.5683 6.87949 14.7354 7.04517C14.9029 7.21108 15.0187 7.43085 15.0185 7.66539L15.0242 15.5669C15.0237 16.0496 14.6329 16.4426 14.1441 16.4426Z"
          fill="currentColor"
        />
      </svg>

      ${map(segments, (seg, i) =>
        html`${i > 0 ? ' & ' : ''}${
          seg.html ? unsafeHTML(seg.html) : seg.plain
        }`
      )}
    </p>`;
  }

  renderInitial() {
    return html`<form
      ${ref((el) => (this.form = el))}
      method=${ifDefined(this.method)}
      ?novalidate=${this.novalidate}
      enctype=${ifDefined(this.enctype)}
      class="${this.id}"
    >
      ${this.renderFormElements()} ${this.renderButton()} ${this.renderErrors()}
    </form>`;
  }

  render() {
    return this.currentStep.render();
  }
}

window.customElements.get('boilerplate-form') ||
  window.customElements.define('boilerplate-form', BoilerplateForm);
